import {
  Badge,
  Dropdown,
  type MenuProps,
  Table,
  type TableProps,
  Typography,
} from 'antd';
import { type ColumnsType } from 'antd/es/table';
import { generatePath, Link } from 'react-router-dom';

import IconArrowRight from '@/assets/icons/arrow-right.svg?react';
import IconMoreHoriz from '@/assets/icons/more-horiz.svg?react';
import { ROUTES_PATH } from '@/constants/routes.constants';
import {
  DEFAULT_DROPDOWN_OPTIONS,
  DEFAULT_PAGINATION_OPTIONS,
} from '@/constants/table.constants';
import type { OrganizationResponseItem } from '@/features/organization/types';
import { useModalMethod } from '@/hooks/use-modal-method';
import { useUserInformation } from '@/hooks/use-user-information';

import { ORGANIZATION_CONTRACT_STATUS_CONTENTS } from '../../constants';
import { useDeleteOrganization } from '../../mutations/organization';

const { Text } = Typography;

interface OrganizationTableProps extends TableProps<OrganizationResponseItem> {
  deleteOrganization: ReturnType<typeof useDeleteOrganization>['mutate'];
}

export function OrganizationTable({
  deleteOrganization,
  ...rest
}: OrganizationTableProps) {
  const { confirm } = useModalMethod();
  const { isSbfMasterOrSbsOperationRole } = useUserInformation();

  async function onDeleteOrganization(id: number) {
    const confirmed = await confirm({
      title: 'チェーンの削除',
      content: 'このチェーンを削除しますか？',
      okText: '削除',
    });
    if (!confirmed) return;
    deleteOrganization(id);
  }

  const items = (id: number): MenuProps['items'] => [
    {
      key: '0',
      label: (
        <Text>
          チェーンの削除
          <IconArrowRight className="ml-24" />
        </Text>
      ),
      onClick: () => onDeleteOrganization(id),
    },
  ];

  const columns: ColumnsType<OrganizationResponseItem> = [
    {
      title: 'チェーン',
      dataIndex: 'name',
      width: '60%',
      ellipsis: true,
      render: (organizationName, { id }) => (
        <Link
          to={generatePath(ROUTES_PATH.organizationDetail, { id: String(id) })}
        >
          <Text underline className="white-space-pre">
            {organizationName}
          </Text>
        </Link>
      ),
    },
    {
      title: 'Aコード',
      dataIndex: 'is_acode_registered',
      width: '17%',
      ellipsis: true,
      render: isAcodeRegistered => (isAcodeRegistered ? '登録済み' : '未登録'),
    },
    {
      title: 'ステータス',
      dataIndex: 'contract_status',
      width: '17%',
      ellipsis: true,
      render: (contractStatus: OrganizationResponseItem['contract_status']) => (
        <Badge {...ORGANIZATION_CONTRACT_STATUS_CONTENTS[contractStatus]} />
      ),
    },
    ...(isSbfMasterOrSbsOperationRole
      ? ([
          {
            width: 60,
            align: 'center',
            className: 'py-0',
            render: (_, { id }) => (
              <Dropdown
                menu={{ items: items(id) }}
                {...DEFAULT_DROPDOWN_OPTIONS}
              >
                <IconMoreHoriz />
              </Dropdown>
            ),
          },
        ] as ColumnsType<OrganizationResponseItem>)
      : []),
  ];

  return (
    <Table
      rowKey="id"
      columns={columns}
      bordered
      {...rest}
      pagination={{ ...DEFAULT_PAGINATION_OPTIONS, ...rest.pagination }}
    />
  );
}
